import React, { useState, useMemo, useEffect } from "react";
import {
  ArrowLeft, ArrowRight, Check, Sparkles, TrendingUp, X, Pencil,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Logo } from "./Shared";
import { QUESTIONS, HINTS, HIGHLIGHT_NAMES, projectGrowth, VISITED_VENUE_OPTIONS } from "../mock";

// 由 mock 取回一致的選項字串（確保與篩選邏輯字串完全相同）
const ALLQ = [...QUESTIONS.step1, ...QUESTIONS.step2, ...QUESTIONS.step3];
const optOf = (key) => (ALLQ.find((q) => q.key === key) || {}).options || [];

// 分段：0 場域現況、1 行銷與目標、2 個人資訊、3 確認送出
export const SECTIONS = ["場域現況", "個人資料", "行銷與目標", "確認送出"];

// 高亮提示文字中的場域名稱
function highlightNames(text) {
  const re = new RegExp(`(${HIGHLIGHT_NAMES.join("|")})`, "g");
  return text.split(re).map((part, i) =>
    HIGHLIGHT_NAMES.includes(part) ? (
      <span key={i} className="font-black text-brand">{part}</span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

// 重點高亮：場域名稱 + 數字(含 %/成/倍/萬) + 關鍵字
const HL_PHRASES = ["空燒", "白白損失營收", "專注現場管理", "最多網紅造訪", "奢華露營區", "高效"];
function highlightRich(text) {
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const numRe = "\\d[\\d.,]*\\s*(?:%|成|倍|萬)";
  const parts = [...HIGHLIGHT_NAMES.map(esc), ...HL_PHRASES.map(esc), numRe];
  const re = new RegExp(`(${parts.join("|")})`, "g");
  const test = new RegExp(`^(?:${parts.join("|")})$`);
  return text.split(re).map((p, i) =>
    p && test.test(p) ? (
      <span key={i} className="font-black text-brand">{p}</span>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}

// 對話式節點：場域現況 → 個人資料 → 行銷與目標 → 確認
const NODES = [
  // 場域現況 (s0)
  { id: "location", s: 0, kind: "radio", key: "location", q: "你的場域在哪裡？", options: optOf("location"), allowOther: true, otherKey: "locationOther" },
  { id: "roomCount", s: 0, kind: "radio", key: "roomCount", q: "有幾間房 / 帳篷？", options: optOf("roomCount") },
  { id: "maxGuests", s: 0, kind: "radio", key: "maxGuests", q: "最多可以住幾人？", options: optOf("maxGuests") },
  { id: "occupancyRate", s: 0, kind: "radio", key: "occupancyRate", q: "目前的住房 / 入住率大約多少？", options: optOf("occupancyRate") },
  { id: "occHint", s: 0, kind: "message", title: "空房、空帳都是燒錢", text: HINTS.occupancy, showIf: (f) => f.occupancyRate && f.occupancyRate !== "80% 以上" },
  { id: "monthlyRevenue", s: 0, kind: "radio", key: "monthlyRevenue", q: "目前每月營收大約？", options: optOf("monthlyRevenue") },
  { id: "projection", s: 0, kind: "projection", showIf: (f) => !!projectGrowth(f.monthlyRevenue) },
  // 個人資料 (s1)
  { id: "visitedLakeze", s: 1, kind: "radio", key: "visitedLakeze", q: "你到過人客多的場域嗎？", options: optOf("visitedLakeze") },
  { id: "visitedVenues", s: 1, kind: "multi", key: "visitedVenues", q: "你到過哪些人客多場域？", subtitle: "可複選，選擇你去過的場域", options: VISITED_VENUE_OPTIONS, showIf: (f) => f.visitedLakeze === "有" },
  { id: "name", s: 1, kind: "text", key: "name", q: "怎麼稱呼你？", placeholder: "你的稱呼", required: true, autoComplete: "name" },
  { id: "venueSocial", s: 1, kind: "text", key: "venueSocial", q: "你的場域 FB / IG 名稱？", placeholder: "例如 IG @your_venue", required: true, hint: "提供場域 FB / IG，能讓我們更快了解你的狀況、更快聯繫你" },
  { id: "lineDisplayName", s: 1, kind: "text", key: "lineDisplayName", q: "你的 LINE 顯示名稱？", placeholder: "LINE 上的顯示名稱", required: true },
  { id: "lineId", s: 1, kind: "text", key: "lineId", q: "你的 LINE ID？", placeholder: "希望更快取得聯繫，請填 LINE ID", required: false },
  { id: "phone", s: 1, kind: "text", key: "phone", q: "方便聯絡你的電話？", placeholder: "0912345678", inputType: "tel", digitsOnly: true, required: true, autoComplete: "tel" },
  { id: "email", s: 1, kind: "email", q: "留下你的 Email" },
  // 行銷與目標 (s2)
  { id: "runningAds", s: 2, kind: "radio", key: "runningAds", q: "目前有在投廣告嗎？", options: optOf("runningAds") },
  { id: "adsHint", s: 2, kind: "message", title: "廣告與網紅，決定你被誰看見", text: HINTS.ads, showIf: (f) => f.runningAds === "沒有" },
  { id: "kolCooperation", s: 2, kind: "radio", key: "kolCooperation", q: "目前有跟網紅合作嗎？", options: optOf("kolCooperation") },
  { id: "kolHint", s: 2, kind: "message", title: "KOL 曝光與合作，帶來真實住房", text: HINTS.kol, showIf: (f) => f.kolCooperation === "沒有" || f.kolCooperation === "曾經合作但效果不好" },
  { id: "targetRevenue", s: 2, kind: "radio", key: "targetRevenue", q: "你希望每月營收達到多少？", options: optOf("targetRevenue") },
  { id: "avgPrice", s: 2, kind: "radio", key: "avgPrice", q: "平均每晚單價大約？", options: optOf("avgPrice") },
  { id: "decisionMaker", s: 2, kind: "radio", key: "decisionMaker", q: "合作與否，由誰決定？", options: optOf("decisionMaker") },
  { id: "review", s: 3, kind: "review", q: "確認一下你的答案" },
];

// 確認頁分組：問卷內容 / 個人資料
const QUESTION_ROWS = [
  ["location", "場域地點"], ["roomCount", "房間/帳篷數"], ["maxGuests", "最多可住人數"],
  ["occupancyRate", "目前住房率"], ["monthlyRevenue", "目前月營收"], ["runningAds", "是否投廣告"],
  ["kolCooperation", "網紅合作"], ["targetRevenue", "目標月營收"], ["avgPrice", "平均單價"],
  ["decisionMaker", "合作決定權"], ["visitedLakeze", "到過人客多場域"], ["visitedVenues", "到過的場域"],
];
const PERSONAL_ROWS = [
  ["name", "稱呼"], ["venueSocial", "場域 FB/IG"], ["lineDisplayName", "LINE 顯示名稱"],
  ["lineId", "LINE ID"], ["phone", "聯絡電話"], ["email", "電子信箱"],
];

export default function ConversationalSurvey({ form, setForm, onBack, onSubmit, submitting, onProgress }) {
  const [currentId, setCurrentId] = useState(NODES[0].id);
  const [error, setError] = useState("");

  const visible = useMemo(() => NODES.filter((n) => !n.showIf || n.showIf(form)), [form]);
  const idx = Math.max(0, visible.findIndex((n) => n.id === currentId));
  const node = visible[idx] || visible[0];
  // 分段進度：只顯示目前 section 的位置，避免一次看起來太多步
  const secNodes = visible.filter((n) => n.s === node.s);
  const secPos = Math.max(0, secNodes.findIndex((n) => n.id === node.id));
  const secTotal = secNodes.length;
  const secFrac = secTotal ? (secPos + 1) / secTotal : 1;

  // 逐題回報進度給後端（追蹤未完成）
  useEffect(() => {
    if (onProgress) {
      const pct = Math.round(((idx + 1) / visible.length) * 100);
      onProgress(form, currentId, pct);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setError("");
  };

  const goTo = (id) => {
    setError("");
    setCurrentId(id);
    window.scrollTo({ top: 0 });
  };
  const goNext = () => {
    const list = NODES.filter((n) => !n.showIf || n.showIf(form));
    const i = list.findIndex((n) => n.id === node.id);
    if (i < list.length - 1) goTo(list[i + 1].id);
  };
  const goPrev = () => {
    if (idx <= 0) return onBack();
    goTo(visible[idx - 1].id);
  };

  // 單選：點擊即選取並自動跳下一題
  const selectRadio = (val) => {
    set(node.key, val);
    if (node.allowOther && val === "其他") return; // 需要補填，先不跳
    setTimeout(() => {
      setCurrentId((cur) => {
        const list = NODES.filter((n) => !n.showIf || n.showIf({ ...form, [node.key]: val }));
        const i = list.findIndex((n) => n.id === cur);
        return i < list.length - 1 ? list[i + 1].id : cur;
      });
      window.scrollTo({ top: 0 });
    }, 260);
  };

  const validateAndNext = () => {
    if (node.kind === "text") {
      if (node.required && !form[node.key]?.trim()) return setError("這題是必填的喔");
      if (node.key === "location" && form.location === "其他" && !form.locationOther?.trim())
        return setError("請輸入場域地點");
    }
    if (node.kind === "email") {
      if (!form.email) return setError("請輸入電子信箱");
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return setError("電子信箱格式不正確");
      if (form.email !== form.emailConfirm) return setError("兩次輸入的信箱不一致");
    }
    if (node.kind === "multi") {
      if (!(form[node.key] || []).length) return setError("請至少選擇一個場域");
    }
    goNext();
  };

  const emailMatch = form.email && form.email === form.emailConfirm;
  const emailMismatch = form.emailConfirm.length > 0 && form.email !== form.emailConfirm;
  const proj = projectGrowth(form.monthlyRevenue);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-white">
      {/* 頂部：分段進度 */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3">
          <Logo className="h-7" />
          <button onClick={onBack} className="rounded-full p-1 text-muted-foreground hover:bg-secondary">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mx-auto flex max-w-xl gap-1.5 px-5 pb-2">
          {SECTIONS.map((_, i) => {
            const fill = i < node.s ? 100 : i === node.s ? secFrac * 100 : 0;
            return (
              <div key={i} className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-brand transition-all duration-500" style={{ width: `${fill}%` }} />
              </div>
            );
          })}
        </div>
      </header>

      {/* 內容 */}
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col px-5 pt-10">
        <p className="mb-4 flex items-center gap-2 text-base font-medium text-brand">
          <span className="rounded-full bg-orange-100 px-3 py-1">{SECTIONS[node.s]}</span>
          {node.kind !== "review" && (
            <span className="text-muted-foreground">本節 {secPos + 1} / {secTotal}</span>
          )}
        </p>

        {/* RADIO */}
        {node.kind === "radio" && (
          <div className="fade-up" key={node.id}>
            <h1 className="mb-8 text-[28px] font-black leading-snug sm:text-4xl">{node.q}</h1>
            <div className="space-y-4">
              {node.options.map((opt) => {
                const active = form[node.key] === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => selectRadio(opt)}
                    className={`flex w-full items-center justify-between rounded-2xl border px-6 py-5 text-left text-lg font-medium transition ${
                      active
                        ? "border-brand bg-orange-50 text-brand ring-2 ring-orange-200"
                        : "border-border bg-white hover:border-orange-300"
                    }`}
                  >
                    {opt}
                    {active && <Check className="h-6 w-6 shrink-0" />}
                  </button>
                );
              })}
            </div>
            {node.allowOther && form[node.key] === "其他" && (
              <div className="mt-4">
                <Input
                  value={form.locationOther}
                  onChange={(e) => set("locationOther", e.target.value)}
                  placeholder="請輸入場域地點"
                  className="h-14 text-lg"
                  autoFocus
                />
              </div>
            )}
          </div>
        )}

        {/* MULTI（可複選） */}
        {node.kind === "multi" && (
          <div className="fade-up" key={node.id}>
            <h1 className="mb-3 text-[28px] font-black leading-snug sm:text-4xl">{node.q}</h1>
            {node.subtitle && <p className="mb-6 text-base text-muted-foreground">{node.subtitle}</p>}
            <div className="space-y-4">
              {node.options.map((opt) => {
                const arr = form[node.key] || [];
                const active = arr.includes(opt);
                return (
                  <button
                    key={opt}
                    onClick={() => {
                      const next = active ? arr.filter((x) => x !== opt) : [...arr, opt];
                      set(node.key, next);
                    }}
                    className={`flex w-full items-center justify-between rounded-2xl border px-6 py-5 text-left text-lg font-medium transition ${
                      active
                        ? "border-brand bg-orange-50 text-brand ring-2 ring-orange-200"
                        : "border-border bg-white hover:border-orange-300"
                    }`}
                  >
                    {opt}
                    <span
                      className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border ${
                        active ? "border-brand bg-brand text-white" : "border-gray-300"
                      }`}
                    >
                      {active && <Check className="h-4 w-4" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* TEXT */}
        {node.kind === "text" && (
          <div className="fade-up" key={node.id}>
            <h1 className="mb-3 text-[28px] font-black leading-snug sm:text-4xl">{node.q}</h1>
            {node.hint && <p className="mb-5 text-base leading-relaxed text-muted-foreground">{node.hint}</p>}
            <Input
              type={node.inputType || "text"}
              inputMode={node.digitsOnly ? "numeric" : undefined}
              autoComplete={node.autoComplete || "off"}
              enterKeyHint="next"
              value={form[node.key]}
              onChange={(e) => set(node.key, node.digitsOnly ? e.target.value.replace(/\D/g, "") : e.target.value)}
              placeholder={node.placeholder}
              className="h-16 text-xl"
              autoFocus
              onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
              onKeyDown={(e) => e.key === "Enter" && validateAndNext()}
            />
          </div>
        )}

        {/* EMAIL（雙重確認） */}
        {node.kind === "email" && (
          <div className="fade-up" key={node.id}>
            <h1 className="mb-3 text-[28px] font-black leading-snug sm:text-4xl">{node.q}</h1>
            <p className="mb-5 text-base leading-relaxed text-muted-foreground">評估結果會寄到這個信箱，請輸入兩次確認。</p>
            <Input
              type="email"
              autoComplete="email"
              inputMode="email"
              enterKeyHint="next"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="your@email.com"
              className="mb-4 h-16 text-xl"
              autoFocus
              onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
            />
            <div className="relative">
              <Input
                type="email"
                autoComplete="email"
                inputMode="email"
                enterKeyHint="done"
                value={form.emailConfirm}
                onChange={(e) => set("emailConfirm", e.target.value)}
                placeholder="再次輸入電子信箱"
                className={`h-16 pr-12 text-xl ${
                  emailMismatch ? "border-red-400" : emailMatch ? "border-green-500" : ""
                }`}
                onKeyDown={(e) => e.key === "Enter" && validateAndNext()}
              />
              {emailMatch && (
                <Check className="absolute right-3 top-1/2 h-6 w-6 -translate-y-1/2 text-green-600" />
              )}
            </div>
            {emailMismatch && <p className="mt-2 text-base text-red-500">兩次輸入的信箱不一致</p>}
          </div>
        )}

        {/* MESSAGE（痛點提示） */}
        {node.kind === "message" && (
          <div className="fade-up" key={node.id}>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-1.5 text-base font-bold text-brand">
              <Sparkles className="h-5 w-5" /> {node.title}
            </div>
            <p className="text-xl leading-loose text-foreground">{highlightRich(node.text)}</p>
          </div>
        )}

        {/* PROJECTION（成長試算） */}
        {node.kind === "projection" && proj && (
          <div className="fade-up" key={node.id}>
            <h1 className="mb-4 text-[28px] font-black leading-snug sm:text-4xl">
              想像一下<span className="text-brand">合作後</span>的你
            </h1>
            <p className="mb-6 text-lg leading-relaxed text-foreground">
              你只要<span className="font-bold">專注現場管理</span>，潛在客戶引流、KOL 曝光與合作、Google SEO、11 小時待命客服 都交給人客多，
              住房 / 入住率有機會衝上 <span className="font-bold text-brand">7-9 成</span>。
            </p>
            <div className="rounded-2xl brand-gradient p-5">
              <p className="text-center text-base font-bold text-[#9A3412]">每個月其實少賺</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-orange-200 bg-white px-3 py-4 text-center">
                  <p className="text-sm text-muted-foreground">小場域</p>
                  <p className="text-2xl font-black text-brand">30–40 萬</p>
                </div>
                <div className="rounded-xl border border-orange-200 bg-white px-3 py-4 text-center">
                  <p className="text-sm text-muted-foreground">大場域</p>
                  <p className="text-2xl font-black text-brand">130–210 萬</p>
                </div>
              </div>
              <div className="mt-4 rounded-xl bg-white/80 p-3 text-center">
                <p className="text-base text-muted-foreground">
                  營收成長約 <span className="font-bold text-brand">1.8–4.8 倍</span>
                </p>
              </div>
            </div>
            <p className="mt-3 text-center text-sm text-muted-foreground">
              ※ 以上皆為估算值，並非實際數字，實際成效會因場域條件而異。
            </p>
          </div>
        )}

        {/* REVIEW（問卷內容 / 個人資料 分開顯示） */}
        {node.kind === "review" && (
          <div className="fade-up space-y-4" key={node.id}>
            <h1 className="text-[28px] font-black sm:text-4xl">{node.q}</h1>
            <ReviewGroup title="問卷內容" rows={QUESTION_ROWS.filter(([k]) => k !== "visitedVenues" || (form.visitedVenues || []).length)} form={form} onEdit={goTo} />
            <ReviewGroup title="個人資料" rows={PERSONAL_ROWS} form={form} onEdit={goTo} />
          </div>
        )}

        {error && <p className="mt-4 text-base font-medium text-red-500">{error}</p>}

        {/* 導覽（單選會自動前進，其餘顯示下一步） */}
        <div
          className="sticky bottom-0 z-20 mt-auto flex gap-3 border-t border-border bg-white pt-4"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <Button variant="outline" onClick={goPrev} className="h-14 flex-1 text-base font-bold">
            <ArrowLeft className="mr-1 h-5 w-5" /> 上一步
          </Button>
          {node.kind === "radio" ? (
            <Button
              onClick={() => {
                if (!form[node.key]) return setError("請選擇一個選項");
                if (node.allowOther && form[node.key] === "其他" && !form[node.otherKey]?.trim())
                  return setError("請輸入場域地點");
                goNext();
              }}
              className="h-14 flex-[2] bg-brand text-base font-bold text-white hover:bg-[#EA580C]"
            >
              下一步 <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
          ) : node.kind === "review" ? (
            <Button onClick={onSubmit} disabled={submitting} className="h-14 flex-[2] bg-brand text-base font-bold text-white hover:bg-[#EA580C]">
              {submitting ? "送出中…" : "提交問卷"} <Check className="ml-1 h-5 w-5" />
            </Button>
          ) : (
            <Button onClick={validateAndNext} className="h-14 flex-[2] bg-brand text-base font-bold text-white hover:bg-[#EA580C]">
              {node.kind === "message" || node.kind === "projection" ? "繼續" : "下一步"}
              <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

function ReviewGroup({ title, rows, form, onEdit }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border">
      <div className="bg-secondary px-4 py-2.5 text-base font-bold text-brand">{title}</div>
      <div className="divide-y divide-border">
        {rows.map(([key, label]) => {
          let val =
            key === "location" && form.location === "其他"
              ? form.locationOther
              : form[key];
          if (Array.isArray(val)) val = val.join("、");
          return (
            <div key={key} className="flex items-center justify-between gap-3 px-4 py-3.5">
              <span className="shrink-0 text-base text-muted-foreground">{label}</span>
              <span className="flex items-center gap-2 text-right text-base font-medium">
                {val || "—"}
                <button onClick={() => onEdit(key)} className="text-brand hover:opacity-70">
                  <Pencil className="h-4 w-4" />
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
